import {
  borgAnalytics,
  Button,
  getChannel,
  Input,
  InputMaxPasswordLength,
  makeLink,
  onEnter,
  useModeParams,
  useTheme,
} from '@ytl/common-web';
import { AxiosError } from 'axios';
import {
  Fragment,
  ReactNode,
  RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { ReactComponent as AppleIcon } from '../assets/icon/apple.svg';
import { ReactComponent as ArrowRightIcon } from '../assets/icon/arrow-right.svg';

import { ReactComponent as FacebookIcon } from '../assets/icon/facebook.svg';
import { ReactComponent as GoogleIcon } from '../assets/icon/google.svg';

import { ReactComponent as BellIcon } from '../assets/icon/notification-bell.svg';
import { ReactComponent as RegisterIcon } from '../assets/icon/register.svg';
import { ReactComponent as SMSIcon } from '../assets/icon/sms.svg';
import { useLanguage } from '../contexts/languageContext';
import { useOpenAm } from '../contexts/openAmContext';
import { OpenAmAuthError } from '../shared/@types/OpenAmInitialData';
import { LoginView, StageFailureType } from '../shared/@types/OpenAmStage';
import { useOpenAmStage } from '../shared/services/useOpenAmStage';
import { isValidEmail } from '../shared/utils/emailValidator';
import { LoginUtils } from '../shared/utils/loginUtils';
import { regLinkSetWorkaround } from '../shared/utils/regLinkSet';

export const UserNamePasswordStage = () => {
  const { language, translation } = useLanguage();
  const { isLoading, setAccountLocked, initParams, setError } = useOpenAm();
  const { loginFromStageWithUsernamePassword } = useOpenAmStage();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [captchaValue, setCaptcha] = useState('');
  const [invalidUsername, setInvalidUsername] = useState(false);
  // const [nonEmailUsername, setNonEmailUsername] = useState(true);
  const [invalidPassword, setInvalidPassword] = useState(false);
  // const [emptyPassword, setEmptyPassword] = useState(true);
  const [loginError, setLoginError] = useState('');
  const [renderRecaptcha, setRenderRecaptcha] = useState(true);

  const usernameRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const prevIsFormValid = useRef(false);

  const { isDark, isWebView } = useTheme();
  const { isYolo } = useModeParams();
  const [needsRedirect, setNeedsRedirect] = useState(true);
  const [showB2bRecaptcha, setShowB2bRecaptcha] = useState(false);

  useEffect(() => {
    borgAnalytics.sendVirtualPageView({
      channel: getChannel(isWebView),
      virtualName: 'Login',
    });
  }, [isWebView]);

  const linkParams = useMemo(() => {
    return [isDark ? `theme=dark` : '', isWebView ? `app=true` : '', isYolo ? `mode=yolo` : '', `locale=${language}`]
      .filter(s => !!s)
      .join('&');
  }, [isDark, isWebView, isYolo, language]);

  const forgottenPasswordLink = useMemo(() => {
    return makeLink(initParams?.forgottenPasswordLink, linkParams);
  }, [initParams, linkParams]);

  const registrationLink = useMemo(() => {
    if (initParams?.registrationLink?.trim() !== '#') {
      return makeLink(initParams?.registrationLink, linkParams);
    }
  }, [initParams, linkParams]);

  const registrationB2bLink = useMemo(() => {
    if (initParams?.registrationB2bLink?.trim() !== '#') {
      return makeLink(initParams?.registrationB2bLink, linkParams);
    }
  }, [initParams, linkParams]);

  const registrationB2bInfoLink = useMemo(() => {
    return makeLink(initParams?.registrationB2bInfoLink, linkParams);
  }, [initParams, linkParams]);

  const socialProviders = useMemo(() => {
    return {
      google: (initParams?.socialProviders || []).find(provider => provider.type === 'Google'),
      facebook: (initParams?.socialProviders || []).find(provider => provider.type === 'Facebook'),
      apple: (initParams?.socialProviders || []).find(provider => provider.type === 'Apple'),
    };
  }, [initParams]);

  const captchaSiteKey = useMemo(() => {
    return initParams?.captchaSiteKey;
  }, [initParams]);

  const captchaInvisible = useMemo(() => {
    return initParams?.captchaInvisible?.toLowerCase() === 'true';
  }, [initParams]);

  const needToRenderCaptcha = renderRecaptcha && captchaSiteKey;
  const isCaptchaValid = !needToRenderCaptcha || !!captchaValue;

  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const isFormValid = useMemo(() => {
    return isValidEmail(username) && password.length > 0;
  }, [username, password]);

  const checkUserNamePassword = useCallback(async () => {
    if (!isValidEmail(username)) {
      setInvalidUsername(true);
      usernameRef.current?.focus();
      return;
    } else {
      setInvalidUsername(false);
    }

    if (!password) {
      // passwordRef.current?.focus();
      return;
    } else {
      setInvalidPassword(false);
    }
  }, [username, password]);

  const sendUserNamePassword = useCallback(async () => {
    let tokenToUse = captchaValue;

    if (captchaInvisible && !tokenToUse && recaptchaRef.current) {
      try {
        const token = await recaptchaRef.current.executeAsync();
        if (token) {
          tokenToUse = token;
          setCaptcha(token);
        }
      } catch (e) {
        console.error('recaptcha error', e);
        setLoginError(translation.loginRegister.login.box.loginError.invalidCaptcha);
      }
    }

    try {
      await loginFromStageWithUsernamePassword(username, password, tokenToUse, failure => {
        borgAnalytics.sendLoginEvent({
          channel: getChannel(isWebView),
          virtualName: 'Login failed',
          method: 'email',
          action: 'login_failed',
          result: 'true',
          reason: failure.stagefailure,
        });
        if (failure.stagefailure === StageFailureType.AccountInvalidCredentials) {
          setLoginError(translation.loginRegister.login.box.loginError.invalidUserNameOrPassword);
        } else {
          setLoginError(translation.loginRegister.login.box.loginError.general);
        }
      });
    } catch (err) {
      if ((err as AxiosError).isAxiosError) {
        const axiosError = err as AxiosError & { response: { data: { message: string }; status: number } };
        const reason = axiosError.response?.data?.message || `${err}`;
        borgAnalytics.sendLoginEvent({
          channel: getChannel(isWebView),
          virtualName: 'Login failed',
          method: 'email',
          action: 'login_failed',
          result: 'true',
          reason,
        });
        if (axiosError.response?.data?.message === StageFailureType.AccountInvalidCredentials) {
          setLoginError(translation.loginRegister.login.box.loginError.invalidUserNameOrPassword);
        } else if (axiosError.response?.data?.message === 'error-account-captcha-invalid') {
          setLoginError(translation.loginRegister.login.box.loginError.invalidCaptcha);
        } else if (
          axiosError.response?.status === 403 &&
          axiosError.response?.data?.message?.startsWith('error-msisdn-verificator')
        ) {
          setError(OpenAmAuthError.LoginError);
        } else if (axiosError.response?.data?.message === 'error-account-invalid-type-b2b') {
          setError(OpenAmAuthError.InvalidTypeB2B);
        } else if (axiosError.response?.data?.message === 'error-account-invalid-type-b2c') {
          setError(OpenAmAuthError.InvalidTypeB2C);
        } else if (axiosError.response?.data?.message === 'error-empty-phone-number-list') {
          setError(OpenAmAuthError.EmptyPhoneNumberList);
        } else if (
          axiosError.response?.status === 403 ||
          axiosError.response?.data?.message === 'error-account-disabled'
        ) {
          debugger;
          setAccountLocked();
        } else {
          setLoginError(translation.loginRegister.login.box.loginError.general);
        }
      } else {
        borgAnalytics.sendLoginEvent({
          channel: getChannel(isWebView),
          virtualName: 'Login failed',
          method: 'email',
          action: 'login_failed',
          result: 'true',
          reason: `${err}`,
        });
        setLoginError(translation.loginRegister.login.box.loginError.general);
      }
    }
  }, [
    translation,
    setAccountLocked,
    loginFromStageWithUsernamePassword,
    username,
    password,
    captchaValue,
    setLoginError,
    setError,
    captchaInvisible,
    isWebView,
  ]);

  const handleKeyUp = useCallback(
    async (e: React.KeyboardEvent) => {
      await checkUserNamePassword();
      onEnter(sendUserNamePassword)(e);
    },
    [checkUserNamePassword, sendUserNamePassword],
  );

  const onLoginWithSMSClicked = useCallback(() => {
    if (initParams?.smsLoginLink) {
      const target = initParams.smsLoginLink;

      window.location.href = `${target}${target.includes('?') ? '&' : '?'}from=${LoginView.LoginForm}&${linkParams}`;
    }
  }, [initParams, linkParams]);

  const onCaptchaChange = (value: any) => {
    setCaptcha(value);
  };

  const handleInputBlur = useCallback(
    (inputRef: RefObject<HTMLInputElement>) => (e: React.FocusEvent) => {
      if (captchaInvisible) {
        if (e.relatedTarget) {
          if (e.relatedTarget.nodeName === 'IFRAME') {
            inputRef.current?.focus();
          }
        }
      }
    },
    [captchaInvisible],
  );

  const orSection: Array<{ children: ReactNode; key: string }> = useMemo(() => {
    const res: Array<{ children: ReactNode; key: string }> = [];

    if (socialProviders.google) {
      res.push({
        key: 'google',
        children: (
          <a
            className={'button button--outlined button--alternate-login'}
            href={socialProviders.google.link}
            rel="noopener noreferrer"
          >
            {translation.loginRegister.login.box.oauthGoogle}
            <GoogleIcon />
          </a>
        ),
      });
    }
    if (socialProviders.facebook) {
      res.push({
        key: 'facebook',
        children: (
          <a
            className={'button button--outlined button--alternate-login'}
            href={socialProviders.facebook.link}
            rel="noopener noreferrer"
          >
            {translation.loginRegister.login.box.oauthFacebook}
            <FacebookIcon />
          </a>
        ),
      });
    }
    if (socialProviders.apple) {
      res.push({
        key: 'apple',
        children: (
          <a
            className={'button button--outlined button--alternate-login'}
            href={socialProviders.apple.link}
            rel="noopener noreferrer"
          >
            {translation.loginRegister.login.box.oauthApple}
            <AppleIcon />
          </a>
        ),
      });
    }

    return res;
  }, [translation, socialProviders]);

  useEffect(() => {
    setRenderRecaptcha(false);
  }, [language]);

  useEffect(() => {
    if (!renderRecaptcha) {
      setRenderRecaptcha(true);
    }
  }, [renderRecaptcha]);

  useLayoutEffect(() => {
    regLinkSetWorkaround(registrationLink);
  }, [registrationLink]);

  useEffect(() => {
    setNeedsRedirect(LoginUtils.yoloRegistrationNeedsRedirect(registrationLink));
  }, [registrationLink]);

  useEffect(() => {
    if (!needsRedirect && showB2bRecaptcha) {
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- needsRedirect is intentionally excluded to prevent duplicate analytics events
  }, [showB2bRecaptcha, isWebView]);

  useEffect(() => {
    // Detect transition from valid to invalid
    if (prevIsFormValid.current === true && isFormValid === false) {
      if (captchaInvisible) {
        setCaptcha('');
        recaptchaRef.current?.reset();
      }
    }

    // Update the previous state
    prevIsFormValid.current = isFormValid;
  }, [isFormValid, captchaInvisible]);

  return (
    <>
      {needsRedirect && <></>}
      {!needsRedirect && !showB2bRecaptcha && (
        <>
          <h3 className={isWebView ? 'small-title' : ''}>
            {isWebView
              ? translation.loginRegister.login.box.headerApp
              : initParams?.realm === 'b2c'
                ? translation.loginRegister.login.box.header
                : translation.loginRegister.login.box.headerb2b}
          </h3>

          <div className="login-page__container__box__inputs">
            <Input
              label={<>{translation.loginRegister.login.box.email}</>}
              aria-label={translation.loginRegister.login.box.email}
              name={'username'}
              type={'text'}
              inputMode={'email'}
              required={true}
              placeholder={isWebView ? 'pelda@e-mail.com' : ''}
              value={username}
              onChange={e => {
                setUsername(e.target.value);
                setLoginError('');
                // setInvalidUsername(false);
              }}
              disabled={isLoading}
              onKeyUp={handleKeyUp}
              hasError={!!loginError || invalidUsername}
              ref={usernameRef}
              onBlur={handleInputBlur(usernameRef)}
            />

            <Input
              label={translation.loginRegister.login.box.password}
              name={'password'}
              type={'password'}
              required={true}
              placeholder={isWebView ? '************' : ''}
              value={password}
              onChange={e => {
                setPassword(e.target.value);
                setLoginError('');
                setInvalidPassword(false);
              }}
              disabled={isLoading}
              onKeyUp={handleKeyUp}
              hasError={!!loginError || invalidPassword}
              maxLength={InputMaxPasswordLength}
              ref={passwordRef}
              onBlur={handleInputBlur(passwordRef)}
            />

            {loginError && <div className="login-page__container__error">{loginError}</div>}
            {invalidUsername && (
              <div className="login-page__container__error">
                {translation.loginRegister.login.box.loginError.invalidEmail}
              </div>
            )}
            {invalidPassword && (
              <div className="login-page__container__error">
                {translation.loginRegister.login.box.loginError.invalidPassword}
              </div>
            )}
          </div>

          {forgottenPasswordLink && (
            <div className="login-page__container__forgot-password-container">
              <a
                href={forgottenPasswordLink}
                className="button button--text"
                rel="noopener noreferrer"
              >
                {translation.loginRegister.login.box.forgottenPassword}
              </a>
            </div>
          )}

          {needToRenderCaptcha && (
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={captchaSiteKey}
              hl={language}
              size={captchaInvisible ? 'invisible' : undefined}
              onChange={onCaptchaChange}
              className={'login-page__container__recaptcha' + (captchaInvisible ? '-invisible' : '')}
              badge={captchaInvisible ? 'bottomright' : undefined}
              onErrored={() => {
                console.error('recaptcha error');
                recaptchaRef.current?.reset();
                setError(OpenAmAuthError.LoginError);
              }}
            />
          )}

          <Button
            variant={'contained'}
            onClick={sendUserNamePassword}
            disabled={
              isLoading || (!captchaInvisible && !isCaptchaValid) || !isFormValid || invalidUsername || invalidPassword
            }
          >
            {translation.loginRegister.login.box.login}
          </Button>

          {initParams?.realm === 'b2b' && isWebView && (
            <>
              <Button
                variant={'outlined'}
                onClick={() =>
                  needToRenderCaptcha
                    ? setShowB2bRecaptcha(true)
                    : (window.location.href = registrationB2bLink ? registrationB2bLink : '')
                }
                disabled={isLoading || !registrationB2bLink}
              >
                {translation.loginRegister.login.box.registerb2b.button}
              </Button>
              <div className="login-page__container__registration-box-b2b">
                <div className="login-page__container__registration-box-b2b__row">
                  <div
                    style={{
                      width: '24px',
                      height: '24px',
                    }}
                  >
                    <BellIcon />
                  </div>
                  <div className="login-page__container__registration-box-b2b__column">
                    <div
                      style={{
                        fontWeight: 'bolder',
                        fontSize: 'normal',
                      }}
                    >
                      {translation.loginRegister.login.box.registerb2b.infoHeader}
                    </div>

                    <div
                      style={{
                        fontWeight: 'lighter',
                        fontSize: 'smaller',
                      }}
                    >
                      {translation.loginRegister.login.box.registerb2b.infoText}
                    </div>
                  </div>
                </div>
                <div
                  style={{
                    fontWeight: 'bolder',
                    fontSize: 'smaller',
                  }}
                >
                  <p
                    dangerouslySetInnerHTML={{
                      __html: translation.loginRegister.login.box.registerb2b.infoLink(registrationB2bInfoLink),
                    }}
                  />
                </div>
              </div>
            </>
          )}

          {(orSection.length > 0 || initParams?.smsLoginLink || registrationLink) && initParams?.realm === 'b2c' && (
            <>
              <div className="login-page__container__divider">
                <span>
                  {initParams?.realm === 'b2c'
                    ? translation.loginRegister.login.box.otherAuthHeader
                    : translation.loginRegister.login.box.otherAuthHeaderb2b}
                </span>
              </div>

              {orSection.map(item => (
                <Fragment key={item.key}>{item.children}</Fragment>
              ))}

              {(initParams?.smsLoginLink || registrationLink) && (
                <div className="login-page__container__alt-actions">
                  {initParams?.smsLoginLink && (
                    <button
                      type="button"
                      onClick={onLoginWithSMSClicked}
                      disabled={isLoading || (!captchaInvisible && !isCaptchaValid)}
                      className="login-page__container__alt-action"
                      aria-label={translation.loginRegister.login.box.authSMS}
                    >
                      <SMSIcon
                        className="login-page__container__alt-action-icon"
                        aria-hidden="true"
                      />
                      <span className="login-page__container__alt-action-label">
                        {translation.loginRegister.login.box.authSMS}
                      </span>
                      <ArrowRightIcon
                        className="login-page__container__alt-action-chevron"
                        aria-hidden="true"
                      />
                    </button>
                  )}

                  {registrationLink && (
                    <a
                      className="login-page__container__alt-action"
                      href={registrationLink}
                      rel="noopener noreferrer"
                      role="button"
                      aria-label={translation.loginRegister.login.box.register}
                    >
                      <RegisterIcon
                        className="login-page__container__alt-action-icon"
                        aria-hidden="true"
                      />
                      <span className="login-page__container__alt-action-label">
                        {translation.loginRegister.login.box.register}
                      </span>
                      <ArrowRightIcon
                        className="login-page__container__alt-action-chevron"
                        aria-hidden="true"
                      />
                    </a>
                  )}
                </div>
              )}
            </>
          )}
        </>
      )}
      {!needsRedirect && showB2bRecaptcha && (
        <>
          <div
            style={{
              fontWeight: 'bolder',
              fontSize: 'xx-large',
            }}
          >
            {translation.loginRegister.login.box.registerb2b.captcha.title}
          </div>
          <div
            style={{
              fontWeight: 'lighter',
              fontSize: 'medium',
            }}
          >
            {translation.loginRegister.login.box.registerb2b.captcha.header}
          </div>
          {needToRenderCaptcha && (
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={captchaSiteKey}
              hl={language}
              size={captchaInvisible ? 'invisible' : undefined}
              onChange={onCaptchaChange}
              className={'login-page__container__recaptcha' + (captchaInvisible ? '-invisible' : '')}
              badge={captchaInvisible ? 'bottomright' : undefined}
              onErrored={() => {
                console.error('recaptcha error');
                recaptchaRef.current?.reset();
                setError(OpenAmAuthError.LoginError);
              }}
            />
          )}
          <Button
            variant={'contained'}
            onClick={() => (window.location.href = registrationB2bLink ? registrationB2bLink : '')}
            disabled={isLoading || !registrationB2bLink || !isCaptchaValid}
          >
            {translation.loginRegister.login.box.registerb2b.captcha.send}
          </Button>
          <Button
            variant={'outlined'}
            onClick={() => setShowB2bRecaptcha(false)}
            disabled={isLoading || !registrationB2bLink}
          >
            {translation.loginRegister.login.box.registerb2b.captcha.back}
          </Button>
        </>
      )}
    </>
  );
};
